import moment from 'moment';
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import { Balance } from '../../components/Balance';
import { HelpText } from '../../components/HelpText';
import { InfoLabel } from '../../components/InfoLabel';
import FlatButton from '../../components/input/FlatButton';
import { PageSection } from '../../components/PageSection';
import { usePopupDispatch } from '../../components/Popup/PopupProvider';
import Spacer from '../../components/Spacer';
import Table from '../../components/Table/Table';
import { formatCurrency } from '../../helpers';
import { SubscriberBilling_account } from './__generated__/SubscriberBilling_account.graphql';

const Container = styled.div``;

const BalanceSection = styled.div`
  display: flex;
  justify-content: center;
`;

const NoCardText = styled.div`
  color: #aaadc1;
  text-align: center;
  font-weight: 300;
  font-size: 15px;
`;

type Props = {
  account: SubscriberBilling_account;
};

function Billing({ account }: Props) {
  const popupDispatch = usePopupDispatch();
  console.log('account', account);

  return (
    <Container>
      <BalanceSection>
        <Balance
          title='Next subscriber invoice'
          amount={formatCurrency(
            account.subscriber.currentInvoiceAmount.currency,
            account.subscriber.currentInvoiceAmount.value
          )}
        >
          <HelpText>
            Invoice amounts are taken from your account at the end of each month
          </HelpText>
        </Balance>
      </BalanceSection>

      <Spacer vertical spacing={5} />
      <PageSection
        title='Subscriber payment method'
        subtitle='Update the card details used to pay for subscriptions.'
        extraItems={
          <FlatButton
            darkenOnHover
            color='#5C6EF8'
            onClick={() => {
              popupDispatch({
                action: 'open',
                popupType: 'update-subscriber-card',
              });
            }}
          >
            {account.subscriber.defaultMethod
              ? 'Update payment method'
              : 'Add payment method'}
          </FlatButton>
        }
      >
        {account.subscriber.defaultMethod && (
          <>
            <InfoLabel label='NAME'>
              {account.subscriber.defaultMethod?.billingName}
            </InfoLabel>
            <InfoLabel label='BRAND'>
              {account.subscriber.defaultMethod?.brand.toUpperCase()}
            </InfoLabel>
            <InfoLabel label='CARD NUMBER'>
              ending in {account.subscriber.defaultMethod?.last4}
            </InfoLabel>
          </>
        )}
        {!account.subscriber.defaultMethod && (
          <NoCardText>No card given</NoCardText>
        )}
      </PageSection>
      <Spacer vertical spacing={5} />
      <PageSection
        title='Subscriber invoices'
        subtitle='Invoices are created monthly and payment is taken automatically.'
      >
        <Spacer vertical spacing={2} />
        <Table
          rows={account.subscriber?.invoices?.map((i) => i) ?? []}
          onRowClick={(row) => {
            window.open(row.pdfURL, '_blank');
          }}
          columns={[
            {
              headerName: 'Title',
              component: (invoice) => (
                <div>
                  Subscriptions {moment(invoice.startDate).format('LL')}
                </div>
              ),
              flex: 1,
            },
            {
              headerName: 'Total Amount',
              component: (invoice) => <div>£{invoice.amountDue / 100}</div>,
            },
            {
              headerName: 'Paid',
              component: (invoice) => (
                <div>{invoice.paid ? 'Paid' : 'Unpaid'}</div>
              ),
            },
          ]}
        />
      </PageSection>
      <Spacer vertical spacing={5} />
    </Container>
  );
}

export default createFragmentContainer(Billing, {
  account: graphql`
    fragment SubscriberBilling_account on Account {
      subscriber {
        defaultMethod {
          brand
          expMonth
          billingName
          expYear
          last4
        }
        currentInvoiceAmount {
          value
          currency
        }
        invoices {
          id
          amountDue
          paid
          dueDate
          startDate
          endDate
          payURL
          pdfURL
        }
      }
    }
  `,
});
